import { addDays, format, parseISO } from 'date-fns';
import { TERMINAL_STATUSES, isTerminal } from '../../../../utils/orderStatusLabel';

const CANCELLED = ['CANCELLED_BY_USER', 'CANCELLED_BY_PROVIDER', 'CANCELLED_PAYMENT_FAILED'];
const BOOKED = ['CONFIRMED', 'PAID', 'PAID_AND_READY', ...TERMINAL_STATUSES];

export const dayKey = (date) => format(date, 'yyyy-MM-dd');

/**
 * A proposal with dates that the customer has not accepted yet. The business status stays
 * DISCUSSION from the proposal through payment verification, so it alone cannot tell; an ACCEPTED
 * quote is a commitment awaiting settlement, not a maybe, and renders solid.
 */
export const isTentative = (order) =>
  !isTerminal(order) && !BOOKED.includes(order.status) && order.quoteStatus !== 'ACCEPTED';

export const isCancelled = (order) => CANCELLED.includes(order?.status);

/**
 * The booked start as the provider sees it: { date: 'yyyy-MM-dd', time: 'HH:mm' }.
 * `serviceStartLocal` is authoritative (contract §10: the snapshotted provider zone). Orders from
 * before the zone snapshot have none and fall back to the browser's reading of the instant.
 */
export function localStart(order) {
  const local = order?.serviceStartLocal;
  if (typeof local === 'string' && local.length >= 16) {
    return { date: local.slice(0, 10), time: local.slice(11, 16) };
  }
  if (order?.serviceStartAt) {
    const d = new Date(order.serviceStartAt);
    return { date: dayKey(d), time: format(d, 'HH:mm') };
  }
  return null;
}

/**
 * The calendar event for an order, or null when it has no place on a calendar. A booking occupies
 * `numOfDays` provider-local calendar dates from its start date (backend ServiceTimeParser.localDates),
 * end-exclusive; there is no hourly duration, so there is no timed-vs-all-day distinction.
 */
export function toEvent(order) {
  if (!order || (order.orderType || 'SERVICE') !== 'SERVICE') return null;
  const start = localStart(order);
  if (!start) return null;
  const n = Math.max(1, Number(order.numOfDays) || 1);
  // parseISO of a bare date is local midnight; addDays is calendar arithmetic, so a DST change
  // inside the span cannot add or drop a date.
  const first = parseISO(start.date);
  const days = Array.from({ length: n }, (_, i) => dayKey(addDays(first, i)));
  return {
    id: order.id,
    order,
    startDay: start.date,
    time: start.time,
    days,
    multiDay: n > 1,
    tentative: isTentative(order),
    cancelled: isCancelled(order),
  };
}

const sortKey = (ev) => `${ev.startDay}T${ev.time}`;

export function eventsFor(orders) {
  return (orders || []).map(toEvent).filter(Boolean).sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : sortKey(a) > sortKey(b) ? 1 : 0));
}

/** { 'yyyy-MM-dd': [event, ...] } — a multi-day booking appears under each of its dates. */
export function bucketByDay(events) {
  const buckets = {};
  events.forEach((ev) => {
    ev.days.forEach((key) => {
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(ev);
    });
  });
  return buckets;
}

export const undatedOrders = (orders) => (orders || []).filter((o) => !toEvent(o));
